import { z } from "zod";

const AITelemetryTraceSchema = z.object({
    // 1. Identity & Routing
    traceId: z.string().uuid().describe("Unique ID for this specific inference call"),
    subscriptionId: z.string().nullable().optional().describe("Tenant ID for filtering"),
    serviceCode: z.string().nullable().optional(),
    userId: z.string().nullable().optional(),
    projectId: z.string().nullable().optional(),
    threadId: z.string().nullable().optional().describe("Groups inferences into a single repeatable context (e.g., conversationId, jobId)"),

    // 2. Caller Context
    callerType: z.enum(['agent', 'skill', 'system', 'direct']).describe("What triggered this LLM call"),
    callerName: z.string().nullable().optional().describe("Name of the Agent or Skill (e.g., 'CustomerSupportAgent')"),
    stepName: z.string().nullable().optional().describe("Specific turn or step (e.g., 'turn_2', 'summarization')"),

    // 3. Inference Details
    modelIdentifier: z.string().describe("The exact model used (e.g., gpt-4o-mini)"),
    providerName: z.string(),
    modalityType: z.string(), // 'chat', 'embedding', etc.

    // 4. Prompt Assembly Blueprint (For Debugging)
    promptBlueprint: z.object({
        baseFile: z.string().optional(),
        includedPartials: z.array(z.string()).optional(),
        dynamicDatasetsLoaded: z.array(z.string()).optional()
    }).nullable().optional(),

    // 5. Performance & Usage Metrics
    requestTimestamp: z.string().datetime(),
    responseTimestamp: z.string().datetime(),
    durationMs: z.number(),
    status: z.enum(['success', 'error']),

    usage: z.object({
        prompt_tokens: z.number().default(0),
        completion_tokens: z.number().default(0),
        total_tokens: z.number().default(0),
        cached_tokens: z.number().optional().describe("Tokens that hit the provider's prefix cache"),
        cache_read_input_tokens: z.number().optional(),
        cache_creation_input_tokens: z.number().optional(),
        uncached_input_tokens: z.number().optional(),
        reasoning_tokens: z.number().optional(),
    }).nullable().optional(),

    calculatedCost: z.number().nullable().optional(),
    cost: z.number().nullable().optional(),
    credentialSource: z.string().nullable().optional(),
    credentialScope: z.string().nullable().optional(),
    credentialId: z.string().nullable().optional(),
    billingMode: z.string().nullable().optional(),
    providerEstimatedCost: z.number().nullable().optional(),
    platformBillableCost: z.number().nullable().optional(),

    contextAssembly: z.any().nullable().optional(),
    stablePromptDigest: z.string().nullable().optional(),
    contextSectionDigests: z.record(z.string()).nullable().optional(),
    cachedInputTokens: z.number().nullable().optional(),
    uncachedInputTokens: z.number().nullable().optional(),
    cacheReadTokens: z.number().nullable().optional(),
    cacheWriteTokens: z.number().nullable().optional(),

    // 6. Raw Payloads
    requestPayload: z.any().nullable().optional().describe("The exact request sent to the AI provider (messages, tools, options)"),
    responsePayload: z.any().nullable().optional().describe("The exact response received"),
    errorDetails: z.any().nullable().optional()
});

export default AITelemetryTraceSchema;
